import { useEffect, useState } from 'react';
import { FORM_CONTROL_CLASS, FORM_LABEL_CLASS } from '@safetyhub/ui';
import type { DaumPostcodeResult } from '../types/daum-postcode';

export interface AddressExtra {
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
  buildingName: string;
  sido: string;
  sigungu: string;
}

export interface AddressData {
  address: string;
  addressDetail: string;
  zonecode: string;
}

interface AddressSearchProps {
  // 기본 주소 (검색 결과)
  address: string;
  // 상세 주소 (직접 입력)
  addressDetail?: string;
  // 우편번호
  zonecode?: string;
  // 변경 핸들러
  onChange: (data: AddressData) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  id?: string;
  // 상세 주소 입력 필드 표시 여부 (기본: true)
  showAddressDetail?: boolean;
  /**
   * 시안 정렬 결 opt-in. 4개 소비처(현장 폼·조직 관리·조직 생성/유형 모달)가 폼마다 다른
   * 세대의 결을 쓰고 있어 기본값을 바꾸면 미정렬 폼이 끌려간다 — 정렬된 폼만 'aligned'를
   * 넘긴다(공유 컴포넌트 appearance opt-in 규약).
   */
  appearance?: 'legacy' | 'aligned';
}

/**
 * aligned 검색 버튼 — 공용 Button primary의 결 복제(그라데이션·glow). Button의 SIZE 축에
 * 42px이 없어서(md 38·lg 44) FORM_CONTROL_CLASS(42px) 옆에 나란히 서려면 리터럴이 필요하다.
 * Button에 42px 축이 생기면 이 상수를 지우고 교체할 것.
 */
const ALIGNED_SEARCH_BUTTON =
  'h-[42px] rounded-[10px] px-4 text-sm font-semibold whitespace-nowrap text-white border border-transparent bg-gradient-to-br from-brand-600 to-brand-500 shadow-brand-glow hover:brightness-[1.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';
const ALIGNED_SEARCH_BUTTON_ERROR =
  'h-[42px] rounded-[10px] px-4 text-sm font-semibold whitespace-nowrap text-white border border-transparent bg-rose-600 hover:bg-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1';

const POSTCODE_SCRIPT_SRC =
  'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

type LoadState = 'loading' | 'ready' | 'error';

function useDaumPostcodeLoader(): LoadState {
  const [state, setState] = useState<LoadState>(() => {
    if (typeof window === 'undefined') return 'loading';
    return window.daum?.Postcode ? 'ready' : 'loading';
  });

  useEffect(() => {
    // deps: [] — 마운트 시 1회 실행. state가 'ready'이면 early return하므로 중복 실행 없음
    if (state === 'ready') return;

    // 이미 로드 완료
    if (window.daum?.Postcode) {
      setState('ready');
      return;
    }

    // 기존 script 태그 확인 (index.html의 것 or 이전 로드 시도)
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="postcode.v2.js"]',
    );

    if (existing) {
      const handleLoad = () => {
        if (window.daum?.Postcode) setState('ready');
        else setState('error');
      };
      const handleError = () => setState('error');

      existing.addEventListener('load', handleLoad);
      existing.addEventListener('error', handleError);

      // 이미 로드 완료된 경우 이벤트가 이미 fired됐을 수 있으므로 100ms 후 한 번 확인
      const timer = setTimeout(() => {
        if (window.daum?.Postcode) setState('ready');
      }, 100);

      return () => {
        existing.removeEventListener('load', handleLoad);
        existing.removeEventListener('error', handleError);
        clearTimeout(timer);
      };
    }

    // 동적 로드 (fallback — index.html에 script가 없는 경우)
    const script = document.createElement('script');
    script.src = POSTCODE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (window.daum?.Postcode) setState('ready');
      else setState('error');
    };
    script.onerror = () => setState('error');
    document.head.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

export default function AddressSearch({
  address,
  addressDetail = '',
  zonecode = '',
  onChange,
  placeholder = '주소를 검색하세요',
  disabled = false,
  label = '주소',
  required = false,
  id = 'address',
  showAddressDetail = true,
  appearance = 'legacy',
}: AddressSearchProps) {
  const loadState = useDaumPostcodeLoader();
  const aligned = appearance === 'aligned';

  const handleSearch = () => {
    if (disabled) return;

    if (loadState === 'loading') return;

    if (loadState === 'error' || !window.daum?.Postcode) {
      alert('주소 검색 서비스 로드에 실패했습니다. 페이지를 새로고침한 후 다시 시도해 주세요.');
      return;
    }

    new window.daum.Postcode({
      oncomplete: (data: DaumPostcodeResult) => {
        // 도로명 주소가 있으면 도로명, 없으면 지번 주소 사용
        const selectedAddress = data.roadAddress || data.jibunAddress;

        // 건물명이 있으면 추가
        const fullAddress = data.buildingName
          ? `${selectedAddress} (${data.buildingName})`
          : selectedAddress;

        onChange({
          address: fullAddress,
          addressDetail, // 기존 상세주소 유지
          zonecode: data.zonecode,
        });
      },
    }).open();
  };

  const handleClear = () => {
    if (disabled) return;
    onChange({
      address: '',
      addressDetail: '',
      zonecode: '',
    });
  };

  const handleAddressDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      address,
      addressDetail: e.target.value,
      zonecode,
    });
  };

  // 우편번호 포함한 표시 주소
  const displayAddress = zonecode ? `(${zonecode}) ${address}` : address;

  const isSearchDisabled = disabled || loadState === 'loading';

  return (
    <div className="space-y-3">
      {/* 기본 주소 (검색 결과) */}
      <div>
        {label && (
          <label
            htmlFor={id}
            className={aligned ? FORM_LABEL_CLASS : 'block text-sm font-medium text-ink-700 mb-1'}
          >
            {label}
            {required && <span className="text-rose-500 ml-1">*</span>}
          </label>
        )}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              id={id}
              value={displayAddress}
              readOnly
              placeholder={placeholder}
              disabled={isSearchDisabled}
              className={
                aligned
                  ? // 읽기 전용 클릭형이라 FORM_CONTROL(흰 바탕) 대신 surface 바탕으로 "입력이
                    // 아니라 열림"을 신호한다 — 치수·테두리·포커스는 FORM_CONTROL과 동일 값.
                    `block w-full h-[42px] rounded-[10px] border border-line px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-100 ${
                      isSearchDisabled
                        ? 'bg-ink-100 text-ink-500 cursor-not-allowed'
                        : 'bg-surface-soft cursor-pointer hover:bg-surface-hover'
                    }`
                  : `w-full px-3 py-2 border border-ink-300 rounded-control bg-ink-50 cursor-pointer
                ${isSearchDisabled ? 'bg-ink-100 text-ink-500 cursor-not-allowed' : 'hover:bg-ink-100'}
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500`
              }
              onClick={handleSearch}
            />
            {address && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-600"
                title="주소 지우기"
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isSearchDisabled}
            className={
              isSearchDisabled
                ? `${aligned ? 'h-[42px] rounded-[10px] px-4 text-sm font-semibold whitespace-nowrap border border-transparent' : 'px-4 py-2 text-sm font-medium rounded-control whitespace-nowrap'} bg-ink-100 text-ink-400 cursor-not-allowed`
                : aligned
                  ? loadState === 'error'
                    ? ALIGNED_SEARCH_BUTTON_ERROR
                    : ALIGNED_SEARCH_BUTTON
                  : `px-4 py-2 text-sm font-medium rounded-control whitespace-nowrap ${
                      loadState === 'error'
                        ? 'bg-rose-600 text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500'
                        : 'bg-brand-600 text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
                    }`
            }
          >
            <span className="flex items-center">
              {loadState === 'loading' ? (
                <>
                  <SpinnerIcon className="w-4 h-4 mr-1 animate-spin" />
                  로드 중
                </>
              ) : loadState === 'error' ? (
                <>
                  <WarningIcon className="w-4 h-4 mr-1" />
                  재시도
                </>
              ) : (
                <>
                  <SearchIcon className="w-4 h-4 mr-1" />
                  검색
                </>
              )}
            </span>
          </button>
        </div>
        <p className="mt-1 text-xs text-ink-500">
          클릭하여 도로명 주소를 검색하세요
        </p>
      </div>

      {/* 상세 주소 (직접 입력) */}
      {showAddressDetail && (
        <div>
          <label
            htmlFor={`${id}-detail`}
            className={aligned ? FORM_LABEL_CLASS : 'block text-sm font-medium text-ink-700 mb-1'}
          >
            상세 주소
          </label>
          <input
            type="text"
            id={`${id}-detail`}
            value={addressDetail}
            onChange={handleAddressDetailChange}
            placeholder="건물명, 층, 호수 등을 입력하세요"
            disabled={disabled}
            className={
              aligned
                ? FORM_CONTROL_CLASS
                : `w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm
              ${disabled ? 'bg-ink-100 text-ink-500 cursor-not-allowed' : ''}
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500`
            }
          />
        </div>
      )}
    </div>
  );
}

// Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}
